import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import PageSEO from "@/components/PageSEO";
import { breadcrumb, summitFaqSchema } from "@/lib/schemas";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, CheckCircle, ExternalLink, GraduationCap, Home, Heart, Star, Rocket } from "lucide-react";
import summitHero from "@/assets/summit-hero.png";
import yellowBlazer from "@/assets/yellow-blazer.png";
import CountdownTimer from "@/components/CountdownTimer";
import ChoosePathModal from "@/components/ChoosePathModal";

const TechFounderSummit = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const whoIsThisFor = [
    {
      title: "You paid thousands for a website you do not own.",
      desc: "You cannot edit it freely, you are locked into monthly fees, and if you leave your brokerage or vendor, everything resets."
    },
    {
      title: "You are overwhelmed by manual work and low quality leads.",
      desc: "You know AI should be handling follow ups, admin, and systems, but instead you are stuck doing it all yourself."
    },
    {
      title: "You have a real idea for a real estate tool or app.",
      desc: "You stopped because you do not code and did not know where to start or who to trust."
    },
    {
      title: "You are ready to build assets, not just close deals.",
      desc: "You want tools, systems, and products that work for you beyond commissions."
    }
  ];

  const faqs = [{
    question: "Is there a replay?",
    answer: "Replays are available for VIP ticket holders only. VIP members get lifetime access to all recordings."
  }, {
    question: "Do I need to know how to code?",
    answer: "No coding experience required! We use no-code and low-code tools that anyone can learn."
  }, {
    question: "Is this for Loan Officers too?",
    answer: "Yes! This summit is designed for both Real Estate Agents AND Loan Officers who want to own their tech stack."
  }];

  return (
    <div className="min-h-screen bg-white font-sans">
      <PageSEO
        title="Tech Founder Summit | Build & Own Your Real Estate Tech"
        description="A 4-day virtual intensive for agents and loan officers. Audit your stack, build AI assistants, and ship real estate tools you actually own."
        canonical="https://marquettabryant.com/tech-founder-summit"
        jsonLd={[summitFaqSchema, breadcrumb([{name:"Home",path:"/"},{name:"Tech Founder Summit",path:"/tech-founder-summit"}])]}
      />
      <Header />
      
      {/* Event Concluded Banner */}
      <div className="fixed top-[100px] left-0 right-0 z-40 bg-[#C5A059] text-[#0D1B2A]">
        <div className="container mx-auto px-4 py-3 text-center font-semibold text-sm md:text-base tracking-wide">
          🎓 This event has concluded — Thank you to everyone who attended!
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="pt-44 pb-20 md:pt-52 md:pb-28 bg-gradient-to-br from-white to-[#fffcf2]">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Side - Flyer Image with Badge */}
            <div className="flex justify-center order-first">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#ff69b4]/20 to-[#1e90ff]/20 rounded-3xl blur-xl" />
                <img 
                  src={summitHero} 
                  alt="Tech Founder Summit Flyer" 
                  className="relative w-full max-w-md rounded-3xl shadow-2xl" 
                />
                <div className="absolute -top-4 -right-4 w-24 h-24 md:w-28 md:h-28 bg-gradient-to-br from-[#d8239b] to-[#ff69b4] rounded-full flex items-center justify-center shadow-xl rotate-12 border-4 border-white">
                  <span className="text-white text-xs md:text-sm font-bold text-center leading-tight px-2">
                    100%<br />DONATION<br />BASED
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right Side - Content */}
            <div className="space-y-6">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#ffd700]/20 to-[#ff69b4]/20 text-[#d8239b] font-bold rounded-full text-sm uppercase tracking-wider border border-[#d8239b]/30">
                ✨ A PAY-WHAT-YOU-CAN EXPERIENCE ✨
              </span>
              
              <span className="inline-block px-4 py-2 bg-[#1e90ff]/10 text-[#1e90ff] font-semibold rounded-full text-sm">
                LIVE VIRTUAL SUMMIT: FEB 10–12 & 17 • 11 AM – 2 PM EST
              </span>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-[#2e4053]">
                Real Estate Professionals:{" "}
                <span className="text-[#d8239b]">Stop Paying for Tools You Do Not Own.</span>
              </h1>
              
              <CountdownTimer />
              
              <div className="space-y-4 text-[#2e4053]/80 leading-relaxed">
                <p>
                  This live build experience shows you how real estate tools are actually created and helps you build assets you control instead of platforms you rent.
                </p>
                <p>
                  You will not just learn AI.<br />
                  You will build systems you can use in your real estate business.
                </p>
                <p>
                  If you have ever paid for software you could not customize, fully understand, or keep long term, this experience was built for you.
                </p>
              </div>
              
              <Button 
                size="lg" 
                className="bg-[#d8239b] hover:bg-[#d8239b]/90 text-white font-semibold px-8 py-6 text-lg group"
                onClick={() => {
                  document.getElementById('contribution-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Choose Your Path
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What This Live Build Experience Gives You */}
      <section className="py-20 bg-[#fffcf2]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2e4053] text-center mb-8">
              What This Live Build Experience Gives You
            </h2>
            <div className="space-y-6 text-lg text-[#2e4053]/80 leading-relaxed">
              <p>
                This live build experience is designed to help real estate professionals create their own lead generation tools, cut unnecessary software costs, and stop relying on platforms they don't control.
              </p>
              <p className="font-semibold text-[#2e4053]">By the end of this experience, you will be able to:</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d8239b] flex-shrink-0 mt-1" />
                  <span>Build your own lead generation tools, so your website and social media traffic turns into real conversations instead of dead clicks</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d8239b] flex-shrink-0 mt-1" />
                  <span>Create simple client-facing tools like calculators, forms, and generators that attract attention and capture leads</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d8239b] flex-shrink-0 mt-1" />
                  <span>Replace paid software and subscriptions by understanding which tools you can build yourself and which ones are actually worth paying for</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#d8239b] flex-shrink-0 mt-1" />
                  <span>Own your systems and software, so your business does not disappear when platforms change, prices increase, or tools shut down</span>
                </li>
              </ul>
              <p className="text-[#2e4053]/80 text-lg pt-4">
                This is not about learning another platform.
              </p>
              <p className="text-center font-semibold text-[#d8239b] text-xl">
                It's about building assets you control and keeping more of your money.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2e4053] text-center mb-4">
              Who This Is For
            </h2>
            <p className="text-center text-[#2e4053]/70 text-lg mb-12">
              This experience is for real estate professionals who recognize themselves in any of the situations below.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {whoIsThisFor.map((item, index) => (
                <div key={index} className="flex items-start gap-4 p-6 bg-[#fffcf2] rounded-2xl shadow-sm">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-lg text-[#2e4053]">{item.title}</h3>
                    <p className="text-[#2e4053]/70">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-[#d8239b] font-semibold text-lg mt-8">
              If any of this sounds familiar, you need to be in the room.
            </p>
          </div>
        </div>
      </section>

      {/* Core Build Outcomes */}
      <section className="py-20 bg-gradient-to-br from-white to-[#fffcf2]">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-[#2e4053] text-center mb-12">
            What You Will Build During the Live Experience
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Outcome 1 */}
            <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
              <h3 className="font-bold text-xl text-[#2e4053] mb-4">AI Tools, Compliance, and Product Foundations</h3>
              <p className="text-[#2e4053]/70 mb-4">
                Learn which AI tools actually work in real estate, how to stay compliant, and how real estate tools are designed behind the scenes.
              </p>
              <p className="text-[#d8239b] font-medium text-sm italic">
                ✓ Leave with vetted tools, compliance guardrails, and clarity
              </p>
            </div>

            {/* Outcome 2 */}
            <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
              <h3 className="font-bold text-xl text-[#2e4053] mb-4">Build Your AI Assistant (Done With You)</h3>
              <p className="text-[#2e4053]/70 mb-4">
                Create your own AI assistant trained in your voice, market, and business to support leads, clients, and daily follow up.
              </p>
              <p className="text-[#d8239b] font-medium text-sm italic">
                ✓ Leave with a working AI assistant you understand and can adjust
              </p>
            </div>

            {/* Outcome 3 */}
            <div className="p-8 bg-white rounded-2xl shadow-lg border border-gray-100">
              <h3 className="font-bold text-xl text-[#2e4053] mb-4">Product Thinking for Real Estate Professionals</h3>
              <p className="text-[#2e4053]/70 mb-4">
                Learn how small tools become real real estate products like lead generators, calculators, and client qualification systems.
              </p>
              <p className="text-[#d8239b] font-medium text-sm italic">
                ✓ Leave knowing how to scope and design real estate digital products
              </p>
            </div>
          </div>

          {/* VIP Day 4 Standalone - Under What You Will Build */}
          <div className="max-w-3xl mx-auto mt-12">
            <div className="relative p-8 bg-gradient-to-br from-[#1a1a2e] to-[#2e4053] rounded-2xl shadow-xl border-2 border-[#d8239b]">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#d8239b] to-[#ff69b4] text-white font-bold text-xs rounded-full">
                VIP ONLY
              </div>
              <div className="flex items-center gap-3 mb-4 mt-2">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#d8239b]/20">
                  <Rocket className="w-6 h-6 text-[#ff69b4]" />
                </div>
                <h3 className="font-bold text-xl text-white">VIP Day 4</h3>
              </div>
              <h4 className="text-lg font-bold text-white mb-4">Build Your First Real Estate Digital Product Prototype</h4>
              <div className="space-y-4 text-white/80 leading-relaxed">
                <p>This VIP only session is where everything comes together.</p>
                <p>You will build a real estate focused digital product prototype live, including a client facing lead generation product designed to capture and qualify leads.</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#ff69b4] flex-shrink-0 mt-0.5" />
                    <span>Leave with a working digital product prototype</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#ff69b4] flex-shrink-0 mt-0.5" />
                    <span>Receive done for you versions you can customize for your market</span>
                  </li>
                </ul>
                <p className="text-[#ff69b4] font-semibold text-sm">This session is available to VIP participants only.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why I Built This - Founder Note */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="relative rounded-3xl border border-[#d8239b]/20 bg-gradient-to-br from-[#fffcf2] to-white p-8 md:p-12 shadow-sm">
              {/* Decorative accent */}
              <div className="absolute top-0 left-8 w-16 h-1 bg-gradient-to-r from-[#d8239b] to-[#ff69b4] rounded-full" />
              
              <h2 className="text-2xl md:text-3xl font-bold text-[#2e4053] mb-6 mt-2">
                Why I Built This
              </h2>
              
              <div className="space-y-4 text-[#2e4053]/80 leading-relaxed text-lg">
                <p>
                  I built this because I am tired of seeing people, especially real estate professionals, being sold expensive products they do not need.
                </p>
                <p>
                  The industry is using the AI boom to sell you "magic" tools that promise everything and quietly drain your wallet.
                </p>
                <p className="font-semibold text-[#2e4053]">
                  Here is the truth they will not tell you.
                </p>
                <p className="text-xl font-bold text-[#d8239b]">
                  You can build your own.
                </p>
                <p>
                  It is not hard.<br />
                  It is not expensive.<br />
                  And it is not time consuming.
                </p>
                <p>
                  You just need someone to show you the blueprint.
                </p>
                <p className="font-semibold text-[#2e4053]">
                  So let's build it.
                </p>
                <p className="text-[#d8239b] font-semibold italic text-xl pt-2">
                  – Marquetta
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="contribution-section" className="py-20 bg-white scroll-mt-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#2e4053] mb-4">
              Choose Your Access
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Supporter Access */}
            <div className="p-8 bg-white rounded-3xl border-2 border-[#d8239b]/30 text-center hover:shadow-lg transition-shadow">
              <h3 className="font-bold text-2xl text-[#2e4053] mb-2">Supporter Access (Donation Based)</h3>
              <p className="text-[#2e4053]/70 text-base mb-6">
                Join the live build experience and participate in real time with other real estate professionals.
              </p>
              <p className="text-[#d8239b] font-semibold text-sm mb-4">
                This access is for live attendance only.
              </p>
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {["$10", "$25", "$50", "$75", "$80"].map((amount) => (
                  <span key={amount} className="px-4 py-2 rounded-full bg-[#d8239b]/10 text-[#d8239b] font-bold text-sm border border-[#d8239b]/20">
                    {amount}
                  </span>
                ))}
              </div>
              <p className="text-[#2e4053]/60 text-sm mb-6">
                This experience is designed exclusively for real estate professionals.
              </p>
              <Button 
                size="lg" 
                variant="outline"
                className="w-full border-2 border-[#d8239b] text-[#d8239b] hover:bg-[#d8239b]/10 font-semibold"
                onClick={() => window.open('https://donate.stripe.com/14A28q1Fjaa44G73ek8k800', '_blank')}
              >
                Choose Your Price
              </Button>
            </div>

            {/* VIP Builder Pass */}
            <div className="relative p-8 bg-gradient-to-br from-[#1a1a2e] to-[#2e4053] rounded-3xl border-2 border-[#d8239b] text-center hover:shadow-xl transition-shadow">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-[#d8239b] to-[#ff69b4] text-white font-bold text-sm rounded-full flex items-center gap-1 shadow-lg">
                <Star className="w-4 h-4" />
                MOST POPULAR
              </div>
              <div className="text-[#ff4444] font-bold text-sm mb-3 mt-2">
                ⚠️ ONLY 25 SEATS AVAILABLE
              </div>
              <h3 className="font-bold text-2xl text-white mb-4">VIP Builder Access</h3>
              <p className="text-[#ff69b4] font-bold text-2xl mb-2">$97 <span className="text-sm font-normal text-white/60">One-Time</span></p>
              
              <div className="text-left space-y-3 mb-6 text-white/90 text-sm leading-relaxed">
                <p>VIP is for real estate professionals who want to stop paying for tools and start owning them.</p>
                <p className="font-semibold text-white">VIP participants will:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#ff69b4] flex-shrink-0 mt-0.5" />
                    <span>Learn how real estate lead tools and digital products are built</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#ff69b4] flex-shrink-0 mt-0.5" />
                    <span>Build their own client facing lead generation product live</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#ff69b4] flex-shrink-0 mt-0.5" />
                    <span>Receive done for you versions of everything built so they can customize and deploy quickly</span>
                  </li>
                </ul>
                <p className="pt-2">This allows you to:</p>
                <ul className="space-y-1 pl-4">
                  <li>• Replace paid lead tools and software</li>
                  <li>• Understand how your systems actually work</li>
                  <li>• Keep control even if you change brokerages or platforms</li>
                </ul>
                <p className="text-[#ff69b4] font-semibold pt-2">VIP includes full replay access.</p>
              </div>

              <Button 
                size="lg" 
                className="w-full bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold shadow-lg"
                onClick={() => window.open('https://buy.stripe.com/dRm9ATdsrbWMd6jev45c400', '_blank')}
              >
                Upgrade to VIP
              </Button>
            </div>
          </div>

        </div>
      </section>

      {/* The Mission Section - Below Ticket Options */}
      <section className="py-20 bg-[#fffcf2] border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <div className="space-y-2">
              <h2 className="text-3xl md:text-4xl font-bold text-[#2e4053]">
                The Mission.
              </h2>
              <p className="text-xl font-semibold text-[#d8239b]">
                The Mabel & Augustus Institute.
              </p>
            </div>
            
            <div className="space-y-6 text-lg text-[#2e4053]/80 leading-relaxed text-left">
              <p>
                This summit is 'Pay What You Can' because knowledge shouldn't have a gatekeeper. But your contribution does more than just unlock a seat—it plants a seed.
              </p>
              <p>
                The Mabel & Augustus Institute was born to honor the legacy of our ancestors by closing the gap for the future. We provide critical funding across three pillars:
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 py-6">
                <div className="flex flex-col items-center text-center p-4">
                  <div className="w-14 h-14 rounded-full bg-[#1e90ff]/10 flex items-center justify-center mb-3">
                    <GraduationCap className="w-7 h-7 text-[#1e90ff]" />
                  </div>
                  <h4 className="font-bold text-[#2e4053]">Education</h4>
                  <p className="text-sm text-[#2e4053]/60">Scholarships for the next generation of builders.</p>
                </div>
                <div className="flex flex-col items-center text-center p-4">
                  <div className="w-14 h-14 rounded-full bg-[#d8239b]/10 flex items-center justify-center mb-3">
                    <Home className="w-7 h-7 text-[#d8239b]" />
                  </div>
                  <h4 className="font-bold text-[#2e4053]">Homeownership</h4>
                  <p className="text-sm text-[#2e4053]/60">Down payment assistance for first-time buyers.</p>
                </div>
                <div className="flex flex-col items-center text-center p-4">
                  <div className="w-14 h-14 rounded-full bg-[#ff69b4]/10 flex items-center justify-center mb-3">
                    <Heart className="w-7 h-7 text-[#ff69b4]" />
                  </div>
                  <h4 className="font-bold text-[#2e4053]">Veterans</h4>
                  <p className="text-sm text-[#2e4053]/60">Support for those who served.</p>
                </div>
              </div>

              <p className="text-center">
                When you build your business today, you are helping build a foundation for someone else tomorrow.
              </p>
            </div>

            <a href="https://webuildfamily.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#1e90ff] hover:text-[#d8239b] font-semibold text-lg transition-colors">
              Visit The Institute
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Instructor & FAQ */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Instructor Bio */}
            <div className="text-center mb-16">
              <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden shadow-xl ring-4 ring-[#d8239b]/20">
                <img src={yellowBlazer} alt="Marquetta Bryant" className="w-full h-full object-cover object-top" />
              </div>
              <h3 className="font-bold text-2xl text-[#2e4053] mb-4">Your Instructor</h3>
              <p className="text-lg text-[#2e4053]/80 leading-relaxed mb-6">
                I'm Marquetta Bryant. The Broker who learned to Code. My mission is to help 1,000 agents break the check-to-check cycle.
              </p>
              <a href="https://marquettabryant.com/about">
                <Button 
                  variant="outline" 
                  className="border-[#d8239b] text-[#d8239b] hover:bg-[#d8239b]/10"
                >
                  Learn more about your instructor
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </div>

            {/* FAQ */}
            <h3 className="font-bold text-2xl text-[#2e4053] text-center mb-8">
              Frequently Asked Questions
            </h3>
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="bg-[#fffcf2] rounded-xl px-6 border border-gray-100">
                  <AccordionTrigger className="text-[#2e4053] font-semibold hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#2e4053]/70">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#0364c4]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Don't Just Use AI. <span className="text-[#ff69b4]">Own The Code.</span>
            </h2>
            <p className="text-lg text-white/90 leading-relaxed">
              Join us for this transformative summit and start building your own tech empire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-8 py-6 text-lg"
                onClick={() => setModalOpen(true)}
              >
                Choose Your Path
              </Button>
            </div>
            <div className="pt-4">
              <a href="https://marquettabryant.com/academy">
                <Button 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                >
                  View our upcoming classes
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Summit Footer */}
      <footer className="py-12 bg-[#2e4053]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div className="text-white/60 text-sm">
              © 2026 Marquetta Bryant. All Rights Reserved.
            </div>
            <div className="flex items-center gap-6">
              <a href="/privacy" className="text-white/60 hover:text-white text-sm transition-colors">
                Privacy Policy
              </a>
              <a href="/terms" className="text-white/60 hover:text-white text-sm transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </footer>

      <ChoosePathModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
};

export default TechFounderSummit;
